import type { KnockoutTreeStage } from "@motain/xpa-proto-files-web/lib/types/kotree";
import type { FC } from "react";
import { KoTreeNode } from "@/xpa-components/ko-tree/ko-tree-node/KoTreeNode";
import { isNonNullable } from "@/types/isNonNullable";
import styles from "./KoTreeStage.module.scss";
import classNames from "classnames";
import { createClass } from "@/xpa-components/ko-tree/ko-tree-stage/createArrowClass";

/**
 * This component has 2 labels, they both present the same information, but only one of them will be visible
 * The label at the top is the label that screen readers will read and the label inside the <li> is inaccessible
 *
 * The reason why both exist is to be able to archive both designs for desktop and mobile, on desktop the label is located on top of the
 * first card of each stage, this means that the "parent" of the label has to be the <li> element for the first card. This solves the implementation,
 * but it breaks the flow of the information, the label should go first and then the list of cards, so we need to keep the first label as
 * a screen reader label and the second label as a visual label for desktop
 *
 * Another reason is the way that list of stages work, the one that decided how big a column will be, is the size of the label, so if
 * there is any translation that is bigger than the minimum size, will expand the column to fit that label.
 */
export type KoTreeStageProps = KnockoutTreeStage & {
  isReverse?: boolean;
  isFinal?: boolean; // they have a unique type of arrow
  isSemiFinal?: boolean;
};

const showArrow = (isHidden: boolean | undefined): boolean => {
  return !(isNonNullable(isHidden) && isHidden);
};

export const KoTreeStage: FC<KoTreeStageProps> = (props) => {
  const {
    label,
    nodes,
    arrows,
    isReverse = false,
    isFinal = false,
    isSemiFinal = false,
  } = props;
  return (
    <>
      <p className={classNames("title-7-medium", styles.accessibleLabel)}>
        {isNonNullable(label) && (
          <span className={styles.labelText}>{label.text}</span>
        )}
      </p>

      <ul className={styles.list}>
        {nodes.map((node, index) => {
          return (
            <li
              key={index}
              className={classNames(
                styles.listItem,
                styles[
                  createClass({
                    isReverse,
                    isFinals: isFinal,
                    isSemifinals: isSemiFinal,
                    isShowArrowFront: showArrow(arrows?.hideForwardArrow),
                    isShowArrowBack: showArrow(arrows?.hideBackArrow),
                  })
                ],
              )}
            >
              {isNonNullable(label) && index === 0 && (
                <p
                  className={classNames(
                    "title-7-medium",
                    styles.inaccessibleLabel,
                  )}
                  aria-hidden="true"
                >
                  <span className={styles.labelText}>{label.text}</span>
                </p>
              )}
              <KoTreeNode {...node} />
            </li>
          );
        })}
      </ul>
    </>
  );
};
